import {
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { Button, type ButtonProps } from "./button";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "destructive" | "default";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Подтвердить",
  cancelLabel = "Отмена",
  variant = "destructive",
  busy,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    },
    [busy, onCancel],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey);
    cancelRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKey]);

  if (!open) return null;

  const confirmVariant: ButtonProps["variant"] =
    variant === "destructive" ? "destructive" : "default";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      data-testid="confirm-dialog"
    >
      <div className="w-full max-w-md rounded-xl border bg-card text-card-foreground shadow-lg">
        <div className="p-6 space-y-2">
          <h2
            id="confirm-dialog-title"
            className="text-lg font-semibold leading-none tracking-tight"
          >
            {title}
          </h2>
          {description && (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            ref={cancelRef}
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
